/** @format */
import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';

import { ModelSearchResultsType } from '@toucan/common/services/search/constants';

import {
    SortByOption,
    SortDirection,
} from '@toucan/client/features/search/config/types';

type UseSortByOptionsReturn = {
    categorySortByOptions: SortByOption[];
    defaultSortByOption?: SortByOption;
};

export const useSortByOptions = (
    outOfStock: boolean,
    type?: ModelSearchResultsType
): UseSortByOptionsReturn => {
    const { t } = useTranslation();

    const isCategoryPage = type === ModelSearchResultsType.CATEGORY;
    const isBrandPage = type === ModelSearchResultsType.BRAND;
    const isBrandOrCategoryPage = isBrandPage || isCategoryPage;

    const categorySortByOptions: SortByOption[] = useMemo(() => {
        const categorySortByOptions: SortByOption[] = [];

        if (!isBrandOrCategoryPage) {
            categorySortByOptions.push({
                title: t('Relevance', { context: 'sortOptions' }),
                value: 'score',
                facet: 'relevance',
                direction: SortDirection.ASC,
            });
        }

        categorySortByOptions.push(
            ...[
                {
                    title: t('A-Z', { context: 'sortOptions' }),
                    value: 'a_z',
                    facet: 'model_name',
                    direction: SortDirection.ASC,
                },
                {
                    title: t('Z-A', { context: 'sortOptions' }),
                    value: 'z_a',
                    facet: 'model_name',
                    direction: SortDirection.DESC,
                },
            ]
        );

        if (!outOfStock) {
            categorySortByOptions.push(
                ...[
                    {
                        title: t('Price high to low', {
                            context: 'sortOptions',
                        }),
                        value: 'price_high_low',
                        facet: 'product_price',
                        direction: SortDirection.DESC,
                    },
                    {
                        title: t('Price low to high', {
                            context: 'sortOptions',
                        }),
                        value: 'price_low_high',
                        facet: 'product_price',
                        direction: SortDirection.ASC,
                    },
                ]
            );
        }

        return categorySortByOptions;
    }, [isBrandOrCategoryPage, outOfStock]);

    const findOptionByFacetValue = (facet: string, value: string) => {
        return categorySortByOptions.find((option) => {
            return option.facet === facet && option.value === value;
        });
    };

    const getDefaultSortByOption = () => {
        if (outOfStock && isBrandOrCategoryPage) {
            return findOptionByFacetValue('model_name', 'a_z');
        }

        if (isBrandOrCategoryPage) {
            return findOptionByFacetValue('product_price', 'price_high_low');
        }

        return findOptionByFacetValue('relevance', 'score');
    };

    const defaultSortByOption = getDefaultSortByOption();

    return {
        defaultSortByOption,
        categorySortByOptions,
    };
};
